<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
//Components
import ImageThumbnail from 'Common/components/elements/images/ImageThumbnail.vue'
import ProgressBar from 'Common/components/elements/feedback/ProgressBar.vue'
import {
  CaretUpIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CameraIcon,
  CircleCheckIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
} from 'Common/components/elements/icons/Icons'
import ToggleImageReviewedButton from './ToggleImageReviewedButton.vue'
//Store
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
//Types
import type { Ref } from 'vue'
import { JeteyeRoutes } from 'Modules/RouterSetup'
import { ImageReviewStates } from 'Modules/types/generic/image/ImageReviewStates'

interface PropTypes {
  pushImageRoute: (imageUuid: string) => void
  showSlider: boolean
}

const emit = defineEmits(['toggleSlider'])
const router = useRouter()
const route = useRoute()
const props = defineProps<PropTypes>()
const element: Ref<HTMLElement> = ref(null)
const thumbnails: Ref<Array<InstanceType<typeof ImageThumbnail>>> = ref(null)
const sliderExpanded: Ref<boolean> = ref(false)

const store = useImageReviewStore()
const { allImagesReviewed, currentImageIndex, images } = storeToRefs(store.imagesStore)
const { getImagesLength, getImagesCompletedLength, getNextImageUuid, getPreviousImageUuid } = store.imagesStore

const imagesCompleteCount = computed<number>(() => {
  return getImagesCompletedLength()
})

const imagesCount = computed<number>(() => {
  return getImagesLength()
})

const selectedThumbnailElement = computed<HTMLElement | undefined>(
  () => thumbnails.value?.find((thumbnail) => thumbnail.selected === true)?.element
)

const scrollLeft = (): void => {
  element.value.scrollLeft -= 200
}

const scrollRight = (): void => {
  element.value.scrollLeft += 200
}

const toggleSliderExpand = (): void => {
  sliderExpanded.value = !sliderExpanded.value
}

const handleClick = (event): void => {
  sliderExpanded.value = false
  props.pushImageRoute(event)
}

const handleReviewCompleteClick = () => {
  router.push({
    name: JeteyeRoutes.SummaryReport,
    query: route.query,
  })
}

const containerClass = computed<string>(() => {
  return sliderExpanded.value ? 'absolute bottom-[3rem] h-[calc(100%-4em)]' : 'absolute bottom-[3rem]'
})

const imageThumbnailContainerClass = computed<string>(() => {
  if (sliderExpanded.value === true) {
    return 'h-[85%]'
  }
  return ''
})

const sliderExpandClass = computed<string>(() => {
  return sliderExpanded.value
    ? 'flex-wrap justify-start content-start gap-[0.5em] px-[0.5em]'
    : 'w-max flex-nowrap justify-center gap-0'
})

watch([selectedThumbnailElement, sliderExpanded], () => {
  if (element.value && selectedThumbnailElement.value && sliderExpanded.value === false) {
    const containerBoundingRect = element.value.getBoundingClientRect()
    const thumbnailBoundingRect = selectedThumbnailElement.value.getBoundingClientRect()

    const scrollLeft =
      element.value.scrollLeft +
      thumbnailBoundingRect.left +
      thumbnailBoundingRect.width / 2 -
      containerBoundingRect.width / 2 -
      containerBoundingRect.left * 2

    element.value.scrollTo({ left: scrollLeft, behavior: 'smooth' })
  }
})

onUnmounted(() => {
  element.value = null
})
</script>

<template>
  <div class="h-full w-full">
    <div
      v-show="props.showSlider"
      id="image-slider"
      class="z-20 w-full block border-x-2 border-y-2 border-black"
      :class="containerClass"
    >
      <div
        v-if="sliderExpanded"
        class="z-50 bg-black bg-opacity-50 h-[15%] w-full hover:cursor-pointer"
        @click="toggleSliderExpand"
      ></div>
      <div class="relative grow overflow-y-auto" :class="imageThumbnailContainerClass">
        <div ref="element" class="overflow-x-scroll scroll-smooth slider-container h-full bg-neutral-900 opacity-95">
          <div class="flex h-full slider" :class="sliderExpandClass">
            <template v-for="(image, i) in images" :key="`${image.imageUuid}`">
              <div class="h-[145px] w-[215px]">
                <ImageThumbnail
                  ref="thumbnails"
                  :uuid="image.imageUuid"
                  :image-description="image.imageName"
                  :image-url="image.thumbnailImageUrl"
                  :viewed="image.reviewState !== ImageReviewStates.NotStarted"
                  :approved="image.reviewState === 'Complete'"
                  :selected="i === currentImageIndex"
                  info-postion="top"
                  @on-click="handleClick"
                />
              </div>
            </template>
          </div>
        </div>

        <div v-if="!sliderExpanded">
          <el-button
            class="absolute left-0 top-0 scroll-button opacity-[.65] hover:opacity-100 h-full"
            type="info"
            @click="scrollLeft"
          >
            <CaretLeftIcon />
          </el-button>
          <el-button
            class="absolute right-0 top-0 scroll-button opacity-[.65] hover:opacity-100"
            type="info"
            @click="scrollRight"
          >
            <CaretRightIcon />
          </el-button>
        </div>
      </div>
    </div>
    <div
      class="h-12 w-full border-x-2 border-black absolute bottom-0 flex shrink flex-row flex-nowrap gap-12 text-white justify-between items-center bg-neutral-300"
    >
      <div class="h-3/4 flex justify-center items-center">
        <div class="h-full border-r-[1px] border-neutral-700 flex justify-center items-center px-6 text-neutral-700">
          <span class="pr-2">
            <CameraIcon />
          </span>

          {{ currentImageIndex + 1 }}/{{ imagesCount }}
        </div>
        <div class="w-52 px-6 flex justify-center items-center h-full">
          <CircleCheckIcon />
          <div class="h-4 px-2 w-40">
            <ProgressBar
              :rounded="true"
              :rounded-progress="true"
              :max="imagesCount"
              :value="imagesCompleteCount"
              bg-color="bg-neutral-700"
            />
          </div>
        </div>
      </div>
      <div>
        <el-button-group class="pr-6">
          <el-button
            round
            type="primary"
            :disabled="getPreviousImageUuid() === null"
            @click="() => props.pushImageRoute(getPreviousImageUuid())"
            ><ArrowLeftIcon
          /></el-button>
          <el-button
            round
            type="primary"
            :disabled="getNextImageUuid() === null"
            @click="() => props.pushImageRoute(getNextImageUuid())"
            ><ArrowRightIcon
          /></el-button>
        </el-button-group>
        <ToggleImageReviewedButton :push-image-route="props.pushImageRoute" />
        <el-button type="success" :disabled="allImagesReviewed === false" @click="handleReviewCompleteClick">
          Complete Review
        </el-button>
      </div>
      <div class="h-3/4 flex flex-row justify-center items-center">
        <div
          class="h-full flex justify-center items-center px-4 border-l-[1px] border-neutral-400/20 cursor-pointer hover:bg-neutral-400/20 transition-colors"
          @click="emit('toggleSlider')"
        >
          <span class="text-sm font-light select-none text-neutral-600">
            {{ props.showSlider ? 'Hide Gallery' : 'Show Gallery' }}
          </span>
        </div>

        <div
          v-if="props.showSlider"
          class="w-10 border-l-[1px] border-neutral-700 h-full flex justify-center items-center"
        >
          <el-button class="text-white grow h-full hover:border-sky-400" link>
            <CaretUpIcon class="text-xl" :class="{ 'rotate-180': sliderExpanded }" @click="toggleSliderExpand" />
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.scroll-button {
  height: 100% !important;
}

.slider-container::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.slider-container {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
